import { useUsersStore } from '@/stores/users';
import { EKioskRouterNames } from '@/types';
import { matchPath, useNavigate } from 'react-router-dom';
import { KioskInactivityModal } from './KioskInactivityModal';
import { useCallback } from 'react';

const notShowInactivityRoutes = [
  EKioskRouterNames.KIOSK,
  `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__UNAVAILABLE}`,
];

/**
 * Handles kiosk inactivity behavior and configures the inactivity modal.
 * This component determines whether inactivity detection should run and
 * what action should happen when the inactivity timer finishes.
 * Behavior:
 * - If a user is logged in, the inactivity modal will log out
 *   after the countdown ends and redirect to the kiosk landing page.
 * - If no user is logged in, the inactivity modal will redirect the user
 *   to the kiosk landing page after the countdown ends.
 * - Inactivity detection is disabled on the kiosk landing page when
 *   there is no authenticated user.
 * @returns KioskInactivity controller component that manages kiosk inactivity flow.
 */
export const KioskInactivity = () => {
  const navigate = useNavigate();

  const { userData, logout } = useUsersStore((state) => ({
    userData: state.userData,
    logout: state.logout,
  }));

  const handleLogOut = useCallback(async () => {
    try {
      if (userData) {
        await logout();
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      navigate(EKioskRouterNames.KIOSK);
    }
  }, [logout, navigate, userData]);

  const shouldNotDetectInactivity =
    notShowInactivityRoutes.some((path) =>
      matchPath(path, location.pathname),
    ) && !userData;

  if (shouldNotDetectInactivity) return null;

  if (userData) {
    return <KioskInactivityModal variant="logout" onTimerEnd={handleLogOut} />;
  }

  return (
    <KioskInactivityModal
      variant="redirect"
      onTimerEnd={() => navigate(EKioskRouterNames.KIOSK)}
    />
  );
};
